<template>
  <screen-grid>
    <screen-card :title="$t('locations.id.locator.section.generalData.title')" display="grid" :is-loading="isLoading">
      <template v-slot:body>
        <screen-block
          :title="$t('locations.id.locator.section.generalData.label.features')"
          icon="category"
          :has-data="hasFeatures"
          ratio="1-2"
          :is-loading="isLoading"
          @click="editFeatures"
        >
          <template v-slot:data>
            <span
              v-for="(feature, idx) in currentFrontOffice.featureList"
              :key="`feature-${idx}`"
              class="location-locator__feature"
            >
              {{ getTitle(feature) }}<template v-if="idx < currentFrontOffice.featureList.length - 1">, </template>
            </span>
          </template>
        </screen-block>
      </template>
    </screen-card>
    <screen-card
      :title="$t('locations.id.locator.section.event.title')"
      ratio="1-2"
      display="grid"
      :is-loading="isLoading"
      :helper="$t('locations.id.locator.section.event.helper')"
    >
      <template v-slot:header-right>
        <skeleton-line v-if="isLoading" height="18px" width="160px" margin-left="16px" />
        <ui-switch
          v-else
          ref="customEvents"
          class="location-locator__toggle"
          id="active-news"
          :input-value="isPersonalizedEvents"
          :value="isPersonalizedEvents"
          :reverse="true"
          align="right"
          @input="personalizeContent($event, 'customEvents', 'event')"
        >
          {{ $t('locations.id.locator.section.event.personalized') }}
        </ui-switch>
      </template>
      <template v-slot:body>
        <template v-if="isLoading">
          <screen-flux v-for="n in 2" :key="`event-skeleton-${n}`" :is-loading="isLoading" ratio="1-2" />
        </template>
        <template v-else>
          <template v-if="isPersonalizedEvents">
            <screen-flux
              v-for="(event, idx) in currentLocationEventArray"
              :key="`event-${idx}`"
              :id="event.id"
              :title="event.title"
              :picture="event.picture"
              :description="event.description"
              :link="event.link"
              icon="link"
              :from="event.from"
              :to="event.to"
              :tags="event.tags"
              :locale="$i18n.locale"
              ratio="1-2"
              :draggable="true"
              :is-draggable="true"
              :is-drag-over-me="isDragOverMe(idx, true, 2)"
              @click="editMedia('event', event)"
              @drop.native="onDrop($event)"
              @dragover.prevent.native="dragover(idx, 2)"
              @dragstart.native="startDrag($event, event.id, idx, 2)"
              @dragend.native="endDrag"
              class="location-locator--is-editable-draggable"
              :class="[
                {
                  'location-locator--is-editable-draggable__active':
                    dragged.start && dragged.index === idx && dragged.grpIdx === 2,
                },
              ]"
            />
            <div
              class="location-locator__no-data"
              v-if="currentLocationEventEmpty"
              v-html="
                $t('common.label.noDataContent', {
                  frontOffice: currentFrontOffice.name,
                  locale: currentFrontOffice.localeName,
                })
              "
            ></div>
          </template>
          <template v-else>
            <screen-flux
              v-for="(event, idx) in currentFrontOffice.event"
              :id="event.id"
              :key="`event-${idx}`"
              :title="event.title"
              :picture="event.picture"
              :description="event.description"
              :link="event.link"
              icon="link"
              :from="event.from"
              :to="event.to"
              :tags="event.tags"
              :locale="$i18n.locale"
              :is-editable="false"
              ratio="1-2"
            />
            <div
              class="location-locator__no-data"
              v-if="currentFrontOfficeEventEmpty"
              v-html="
                $t('common.label.noDataContent', {
                  frontOffice: currentFrontOffice.name,
                  locale: currentFrontOffice.localeName,
                })
              "
            ></div>
          </template>
        </template>
        <div class="location-locator__actions">
          <skeleton-input v-if="isLoading" width="228px" />
          <ui-button
            v-else
            button="primary"
            variant="data"
            icon="add_post"
            :label="$t('locations.id.locator.section.event.button.addEvent')"
            :disabled="!customEvents"
            @click="editMedia('event')"
          />
        </div>
      </template>
    </screen-card>
    <screen-card
      :title="$t('locations.id.locator.section.headline.title')"
      display="grid"
      ratio="1-2"
      :is-loading="isLoading"
      :helper="$t('locations.id.locator.section.event.helper')"
    >
      <template v-slot:header-right>
        <skeleton-line v-if="isLoading" height="18px" width="160px" margin-left="16px" />
        <ui-switch
          v-else
          ref="customPictures"
          class="location-locator__toggle"
          id="active-pictures"
          :input-value="isPersonalizedPictures"
          :value="isPersonalizedPictures"
          align="right"
          :reverse="true"
          @input="personalizeContent($event, 'customPictures', 'hotPictures')"
        >
          {{ $t('locations.id.locator.section.headline.personalized') }}
        </ui-switch>
      </template>
      <template v-slot:body>
        <template v-if="isLoading">
          <screen-flux v-for="n in 3" :key="`event-skeleton-${n}`" :is-loading="isLoading" ratio="1-2" />
        </template>
        <template v-else>
          <template v-if="currentLocation.hotPictures && currentLocation.hotPictures.length > 0">
            <screen-flux
              v-for="(hotPicture, idx) in currentLocation.hotPictures"
              :id="hotPicture.id"
              :key="`hot-picture-${idx}`"
              :title="hotPicture.name"
              :picture="hotPicture.picture_url"
              :description="hotPicture.description"
              :link="hotPicture.url"
              icon="link"
              ratio="1-2"
              @click="editHotPicture('hotPictures', hotPicture)"
              :is-draggable="true"
              :is-drag-over-me="isDragOverMe(idx, true, 3)"
              @drop.native="onDrop($event, null, 'hotPictures')"
              @dragover.prevent.native="dragover(idx, 3)"
              @dragstart.native="startDrag($event, hotPicture.id, idx, 3, 3)"
              @dragend.native="endDrag"
              class="location-locator--is-editable-draggable"
              :class="[
                {
                  'location-locator--is-editable-draggable__active':
                    dragged.start && dragged.index === idx && dragged.grpIdx === 3,
                },
              ]"
            />
          </template>
          <template
            v-else-if="currentFrontOffice.hotPictures && currentFrontOffice.hotPictures.length > 0 && !customPictures"
          >
            <screen-flux
              v-for="(hotPicture, idx) in currentFrontOffice.hotPictures"
              :key="`hot-picture-${idx}`"
              :title="hotPicture.name"
              :picture="hotPicture.picture_url"
              :description="hotPicture.description"
              :link="hotPicture.url"
              :is-editable="false"
              icon="link"
              ratio="1-2"
            />
          </template>
          <div
            class="location-locator__no-data"
            v-else
            v-html="
              $t('common.label.noDataContent', {
                frontOffice: currentFrontOffice.name,
                locale: currentFrontOffice.localeName,
              })
            "
          ></div>
        </template>
        <div class="location-locator__actions">
          <skeleton-input v-if="isLoading" width="228px" />
          <ui-button
            v-else
            button="primary"
            variant="data"
            icon="add_photo"
            :label="$t('locations.id.locator.section.headline.button.addHeadline')"
            :disabled="!customPictures || currentLocation.hotPictures.length >= currentFrontOffice.hotMaxPictures"
            @click="editHotPicture('hotPictures')"
          />
        </div>
      </template>
    </screen-card>
    <screen-card
      :title="$t('locations.id.locator.section.team.title')"
      ratio="1-2"
      display="grid"
      :is-loading="isLoading"
    >
      <template v-slot:body>
        <template v-if="isLoading">
          <screen-flux v-for="n in 2" :key="`event-skeleton-${n}`" :is-loading="isLoading" ratio="1-2" />
        </template>
        <template v-else>
          <screen-flux
            v-for="(team, idx) in currentLocation.team"
            :key="`team-${idx}`"
            :title="team.title"
            :picture="team.picture"
            :description="team.description"
            :link="team.link"
            icon="link"
            ratio="1-2"
            @click="editMedia('team', team)"
          />
          <div
            class="location-locator__no-data"
            v-if="!currentLocation.team || currentLocation.team.length === 0"
            v-html="
              $t('common.label.noDataAlone', {
                locale: currentFrontOffice.localeName,
              })
            "
          ></div>
        </template>
        <div class="location-locator__actions">
          <skeleton-input v-if="isLoading" width="228px" />
          <ui-button
            v-else
            button="primary"
            variant="data"
            icon="add_person"
            :label="$t('locations.id.locator.section.team.button.addMember')"
            @click="editMedia('team')"
          />
        </div>
      </template>
    </screen-card>
    <screen-card
      :title="$t('locations.id.locator.section.photos.title')"
      ratio="1-2"
      display="grid"
      :is-loading="isLoading"
      :helper="$t('locations.id.locator.section.event.helper')"
    >
      <template v-slot:header-right>
        <skeleton-line v-if="isLoading" height="18px" width="160px" margin-left="16px" />
        <ui-switch
          v-else
          ref="customPhotos"
          class="location-locator__toggle"
          id="active-photos"
          :input-value="isPersonalizedPhotos"
          :value="isPersonalizedPhotos"
          :reverse="true"
          align="right"
          @input="personalizeContent($event, 'customPhotos', 'locationPictures')"
        >
          {{ $t('locations.id.locator.section.photos.personalized') }}
        </ui-switch>
      </template>
      <template v-slot:body>
        <template v-if="isLoading">
          <screen-flux v-for="n in 2" :key="`photo-skeleton-${n}`" :is-loading="isLoading" ratio="1-2" />
        </template>
        <template v-else>
          <template v-if="currentLocation.formatted_pictures && currentLocation.formatted_pictures.length > 0">
            <screen-flux
              v-for="(event, idx) in currentLocation.formatted_pictures"
              :key="`picture-${idx}`"
              :id="event.id"
              :picture="event.picture"
              ratio="1-2"
              @click="editPicture('locationPictures', event.picture)"
              :is-draggable="true"
              :is-drag-over-me="isDragOverMe(idx, true, 1)"
              @drop.native="onDrop($event, null, 'formatted_pictures')"
              @dragover.prevent.native="dragover(idx, 1)"
              @dragstart.native="startDrag($event, event.id, idx, 1, 1)"
              @dragend.native="endDrag"
              class="location-locator--is-editable-draggable"
              :class="[
                {
                  'location-locator--is-editable-draggable__active':
                    dragged.start && dragged.index === idx && dragged.grpIdx === 1,
                },
              ]"
            />
          </template>
          <template
            v-else-if="
              currentFrontOffice.locationPictures && currentFrontOffice.locationPictures.length > 0 && !customPhotos
            "
          >
            <screen-flux
              v-for="(picture, idx) in currentFrontOffice.locationPictures"
              :key="`picture-${idx}`"
              :picture="picture"
              :is-editable="false"
              ratio="1-2"
            />
          </template>
          <div
            class="location-locator__no-data"
            v-else
            v-html="
              $t('common.label.noDataContent', {
                frontOffice: currentFrontOffice.name,
                locale: currentFrontOffice.localeName,
              })
            "
          ></div>
        </template>
        <div class="location-locator__actions">
          <skeleton-input v-if="isLoading" width="228px" />
          <ui-button
            v-else
            button="primary"
            variant="data"
            icon="add_photo"
            :label="$t('locations.id.locator.section.photos.button.addPhoto')"
            :disabled="!customPhotos"
            @click="editPicture('locationPictures')"
          />
        </div>
      </template>
    </screen-card>
    <screen-card
      :title="$t('locations.id.locator.section.seoContent.title')"
      ratio="1-1"
      display="grid"
      :is-loading="isLoading"
    >
      <template v-slot:header-right>
        <skeleton-line v-if="isLoading" height="18px" width="160px" margin-left="16px" />
        <ui-switch
          v-else
          ref="customSeo"
          class="location-locator__toggle"
          id="active-seo"
          :input-value="isPersonalizedSeo"
          :value="isPersonalizedSeo"
          :reverse="true"
          align="right"
          @input="personalizeContent($event, 'customSeo', 'seo')"
        >
          {{ $t('locations.id.locator.section.seoContent.personalized') }}
        </ui-switch>
      </template>
      <template v-slot:body v-if="isPersonalizedSeo">
        <screen-block
          :title="$t('locations.id.locator.section.seoContent.label.description')"
          icon="text"
          :has-data="typeof currentLocation.description === 'string' && currentLocation.description !== ''"
          :data="currentLocation.description"
          ratio="1-3"
          :is-loading="isLoading"
          @click="editSeoContent('description')"
        />
        <screen-block
          :title="$t('locations.id.locator.section.seoContent.label.metaDescription')"
          icon="text"
          :has-data="typeof currentLocation.metaDescription === 'string' && currentLocation.metaDescription !== ''"
          :data="currentLocation.metaDescription"
          ratio="1-3"
          :is-loading="isLoading"
          @click="editSeoContent('metaDescription')"
        />
        <screen-block
          :title="$t('locations.id.locator.section.seoContent.label.metaTitle')"
          icon="text"
          :has-data="typeof currentLocation.metaTitle === 'string' && currentLocation.metaTitle !== ''"
          :data="currentLocation.metaTitle"
          ratio="1-3"
          :is-loading="isLoading"
          @click="editSeoContent('metaTitle')"
        />
      </template>
      <template v-slot:body v-else>
        <screen-block
          :title="$t('locations.id.locator.section.seoContent.label.description')"
          icon="text"
          :has-data="
            currentFrontOffice.locationDescription !== null &&
            typeof currentFrontOffice.locationDescription[currentLocation.locationType] === 'string' &&
            currentFrontOffice.locationDescription[currentLocation.locationType] !== ''
          "
          :data="
            currentFrontOffice.locationDescription
              ? currentFrontOffice.locationDescription[currentLocation.locationType]
              : ''
          "
          ratio="1-3"
          :is-loading="isLoading"
          :is-editable="false"
        />
        <screen-block
          :title="$t('locations.id.locator.section.seoContent.label.metaDescription')"
          icon="text"
          :has-data="
            currentFrontOffice.locationMetaDescription !== null &&
            typeof currentFrontOffice.locationMetaDescription[currentLocation.locationType] === 'string' &&
            currentFrontOffice.locationMetaDescription[currentLocation.locationType] !== ''
          "
          :data="
            currentFrontOffice.locationMetaDescription
              ? currentFrontOffice.locationMetaDescription[currentLocation.locationType]
              : ''
          "
          ratio="1-3"
          :is-loading="isLoading"
          :is-editable="false"
        />
        <screen-block
          :title="$t('locations.id.locator.section.seoContent.label.metaTitle')"
          icon="text"
          :has-data="
            currentFrontOffice.locationMetaTitle !== null &&
            typeof currentFrontOffice.locationMetaTitle[currentLocation.locationType] === 'string' &&
            currentFrontOffice.locationMetaTitle[currentLocation.locationType] !== ''
          "
          :data="
            currentFrontOffice.locationMetaTitle
              ? currentFrontOffice.locationMetaTitle[currentLocation.locationType]
              : ''
          "
          ratio="1-3"
          :is-loading="isLoading"
          :is-editable="false"
        />
      </template>
    </screen-card>
  </screen-grid>
</template>

<script>
import UiSwitch from '@/components/UI/Switch.vue'
import UiButton from '@/components/UI/Button.vue'
import ScreenGrid from '@/components/Screen/Grid.vue'
import ScreenCard from '@/components/Screen/Card.vue'
import ScreenBlock from '@/components/Screen/Block.vue'
import ScreenFlux from '@/components/Screen/Flux.vue'
import SkeletonLine from '@/components/Skeleton/Line.vue'
import SkeletonInput from '@/components/Skeleton/Input.vue'
import reverse from 'lodash/reverse'
import DragMixin from '@/mixins/drag.mixin'

export default {
  name: 'LocationLocator',
  components: {
    UiSwitch,
    UiButton,
    ScreenCard,
    ScreenBlock,
    ScreenGrid,
    ScreenFlux,
    SkeletonLine,
    SkeletonInput,
  },
  mixins: [DragMixin],
  props: {
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    currentLocation: {
      type: Object,
      required: true,
    },
    currentFrontOffice: {
      type: Object,
      required: true,
    },
    currentClient: {
      type: Object,
      required: true,
    },
    customEvents: {
      type: Boolean,
      required: true,
    },
    customPictures: {
      type: Boolean,
      required: true,
    },
    customPhotos: {
      type: Boolean,
      required: true,
    },
    customSeo: {
      type: Boolean,
      required: true,
    },
    currentClientFeatureList: {
      type: Array,
      required: false,
      default: () => [],
    },
    frontOfficeFeatureList: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      reverse,
    }
  },
  computed: {
    isPersonalizedSeo() {
      return (
        (this.currentLocation.description && this.currentLocation.description !== '') ||
        (this.currentLocation.metaDescription && this.currentLocation.metaDescription !== '') ||
        (this.currentLocation.metaTitle && this.currentLocation.metaTitle !== '') ||
        this.customSeo
      )
    },
    isPersonalizedPhotos() {
      return (this.currentLocation.pictures && this.currentLocation.pictures.length > 0) || this.customPhotos
    },
    isPersonalizedPictures() {
      return (this.currentLocation.hotPictures && this.currentLocation.hotPictures.length > 0) || this.customPictures
    },
    isPersonalizedEvents() {
      return this.currentLocation?.eventCustom.toString() === '1'
    },
    hasFeatures() {
      let bool = false
      if (this.currentLocation.featureList && Object.keys(this.currentLocation.featureList).length > 0) {
        for (const key in this.currentLocation.featureList) {
          if (this.currentLocation.featureList[key].length > 0) {
            bool = true
            break
          }
        }
      }
      return bool
    },
    currentLocationEventArray() {
      return Array.isArray(this.currentLocation.event) ? this.currentLocation.event : []
    },
    currentLocationEventEmpty() {
      return (
        !Array.isArray(this.currentLocation.event) ||
        !this.currentLocation.event ||
        this.currentLocation.event?.length <= 0
      )
    },
    currentFrontOfficeEventEmpty() {
      return !this.currentFrontOffice.event || this.currentFrontOffice.event?.length <= 0
    },
  },
  methods: {
    getTitle(feature) {
      let result = feature
      if (this.frontOfficeFeatureList !== null) {
        this.frontOfficeFeatureList.filter(featureListItem =>
          featureListItem.key === feature ? (result = featureListItem.title) : null
        )
      }
      return result
    },
    editSimpleField(objKey, type, mode, required = false, number = null, options = []) {
      this.$emit('editSimpleField', {
        objKey,
        parameters: {
          type,
          mode,
          required,
          number,
          options,
          helpers: {
            title: this.$t('locations.id.locator.section.seoContent.helpers.info'),
            subtitle: this.$t('locations.id.locator.section.seoContent.helpers.variables'),
          },
        },
      })
    },
    editFeatures() {
      this.$emit('editFeatures', {
        activeList: this.currentLocation.featureList,
        features: this.currentClientFeatureList,
        parameters: {
          mode: 'location',
        },
      })
    },
    personalizeContent(value, ref, type) {
      const parameters = {
        type,
        element: this.$refs[ref].$el.getElementsByTagName('input')[0],
      }
      if (ref === 'customEvents') {
        parameters.eventCustom = value ? '1' : '0'
      }

      if (value) {
        this.$emit('showConfirm', {
          parameters: {
            ...parameters,
            mode: 'clone',
          },
        })
      } else {
        this.$emit('showConfirm', {
          parameters: {
            ...parameters,
            mode: 'reset',
          },
        })
      }
    },
    editMedia(type, media) {
      this.$emit('editMedia', {
        media: media || { frontOfficeId: this.currentFrontOffice.id },
        parameters: {
          type,
        },
      })
    },
    editHotPicture(type, media) {
      this.$emit('editMedia', {
        media: media
          ? {
              id: media.id,
              picture: media.picture_url,
              alt: media.alt,
              link: media.url,
              title: media.name,
              titleSeo: media.titleSeo,
            }
          : { frontOfficeId: this.currentFrontOffice.id },
        parameters: {
          type,
        },
      })
    },
    editPicture(type, media) {
      this.$emit('editMedia', {
        media: media
          ? {
              id: 1,
              picture: media,
            }
          : { frontOfficeId: this.currentFrontOffice.id },
        parameters: {
          type,
        },
      })
    },
    editSeoContent(objKey) {
      this.$emit('editSeoContent', {
        objKey,
        parameters: {
          type: this.currentLocation.locationType,
          helpers: {
            title: this.$t('locations.id.locator.section.seoContent.helpers.info'),
            subtitle: this.$t('locations.id.locator.section.seoContent.helpers.variables'),
            location: this.$t('locations.id.locator.section.seoContent.helpers.location'),
            city: this.$t('locations.id.locator.section.seoContent.helpers.city'),
            shortest: this.$t('locations.id.locator.section.seoContent.helpers.shortest'),
            longest: this.$t('locations.id.locator.section.seoContent.helpers.longest'),
            characters: this.$t('locations.id.locator.section.seoContent.helpers.characters'),
            recommended: this.$t('locations.id.locator.section.seoContent.helpers.recommended'),
            help: this.$t('locations.id.locator.section.seoContent.helpers.help'),
            to: this.$t('locations.id.locator.section.seoContent.helpers.to'),
            char: this.$t('locations.id.locator.section.seoContent.helpers.char'),
            max: this.$t('locations.id.locator.section.seoContent.helpers.max'),
            moreVariables: this.$t('locations.id.locator.section.seoContent.helpers.moreVariables'),
          },
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.location-locator {
  &--is-editable-draggable {
    &:hover {
      outline: 2px solid $blue-royal;
      box-shadow: 0 0 16px rgba(0, 0, 0, 0.4);
      cursor: grab;
    }

    &__active {
      transition: all $transition-duration-very-fast $transition-effect-default;
      opacity: 0.1;
    }
  }

  &__feature {
    text-transform: capitalize;
  }

  &__flux {
    display: flex;
    flex-direction: column;
    margin: (-$gutter-tablet / 2) (-$gutter-mobile / 2);

    @media (min-width: $screen-sm) {
      flex-direction: row;
      flex-wrap: wrap;
      margin: -12px;
    }

    @media (min-width: $screen-lg) {
      flex-direction: row;
      flex-wrap: wrap;
      margin: $generic-button-offset;
    }

    &__item {
      display: flex;
      position: relative;
      flex-direction: column;
      margin: $gutter-tablet / 2 $gutter-mobile / 2;
      margin-bottom: $gutter-mobile;
      border-radius: 6px;
      box-shadow: 0 0 8px var(--box-shadow-color);
      width: calc(100% - #{$gutter-mobile});
      overflow: hidden;

      @media (min-width: $screen-sm) {
        margin: 12px;
        width: calc(100% / 2 - 24px);
      }

      &:hover {
        box-shadow: 0 0 16px var(--box-shadow-color);
        background-color: var(--bg-color-hover);
        cursor: pointer;
      }

      &__img {
        display: inline-flex;
        width: 100%;

        .location-locator__flux__item:hover & {
          filter: brightness(0.8);
        }
      }

      &__title {
        margin: 12px 12px 4px;
        font-weight: 600;
      }

      &__content {
        @include text-ellipsis(3, 14px);

        margin: 4px 12px 12px;
        color: var(--text-color-alt);
      }

      &__link {
        display: flex;
        align-items: center;
        margin: 4px 12px 12px;

        &__icon {
          margin-right: 8px;
          color: map-get($generic-color-variants, 'linkedin');
          font-size: $font-size-lg;
        }

        &__url {
          color: var(--text-color-alt);
          font-size: $font-size-sm;
        }
      }
    }
  }

  &__no-data {
    margin: $gutter-mobile / 2;
    color: var(--text-color-alt);
    font-style: italic;

    @media (min-width: $screen-sm) {
      margin: $gutter-tablet / 2;
    }

    @media (min-width: $screen-xl) {
      margin: $gutter-desktop / 2;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: $gutter-mobile / 2;
    margin-bottom: $gutter-mobile / 2;
    width: 100%;

    @media (min-width: $screen-sm) {
      margin-top: $gutter-tablet / 2;
      margin-bottom: $gutter-tablet / 2;
    }

    @media (min-width: $screen-xl) {
      margin-top: $gutter-desktop / 2;
      margin-bottom: $gutter-desktop / 2;
    }
  }
}
</style>
